import React, { useState, useEffect, useRef } from "react";
import TRANSMISSIONS from "./transmissions";
import "./styles.css";

const LINKS = {
  spotify: "https://open.spotify.com/artist/7sNtmzdnpyNW1iWOSgBrkG",
  apple: "https://music.apple.com/us/artist/unravel-music/1450748356",
  instagram: "https://www.instagram.com/unravelmusic_/",
};

function Transmission({ tx }) {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    if (!audioRef.current || !duration) return;
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className={`tx ${open ? "tx-open" : ""}`} onClick={() => setOpen(!open)}>
      <div className="tx-left">
        <span className="tx-num">{tx.id}</span>
        <span className="tx-type">{tx.type}</span>
      </div>
      <div className="tx-center">
        <div className="tx-title-row">
          <h3 className="tx-title">{tx.title}</h3>
          {tx.latest && <span className="tx-latest">Latest</span>}
        </div>
        {tx.desc && <p className="tx-desc">{tx.desc}</p>}
        {tx.tracks && (
          <div className="tx-tracks">
            {tx.tracks.map((t, i) => (
              <span key={i} className="tx-track">{t}</span>
            ))}
          </div>
        )}
        {!open && tx.excerpt && <p className="tx-desc">Tap to read</p>}
        {!open && tx.audioSrc && <p className="tx-desc">Tap to listen</p>}
        {open && tx.excerpt && (
          <blockquote className="tx-excerpt">{tx.excerpt}</blockquote>
        )}
        {open && tx.audioSrc && (
          <div className="tx-player" onClick={(e) => e.stopPropagation()}>
            <audio
              ref={audioRef}
              src={tx.audioSrc}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setPlaying(false)}
            />
            <button className="tx-play-btn" onClick={togglePlay}>
              {playing ? (
                <svg viewBox="0 0 24 24" width="18" height="18"><rect x="6" y="4" width="4" height="16" fill="currentColor"/><rect x="14" y="4" width="4" height="16" fill="currentColor"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18"><polygon points="5,3 19,12 5,21" fill="currentColor"/></svg>
              )}
            </button>
            <div className="tx-progress-bar" onClick={handleSeek}>
              <div className="tx-progress-fill" style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }} />
            </div>
            <span className="tx-time">{formatTime(progress)} / {formatTime(duration)}</span>
          </div>
        )}
        {open && tx.spotifyId && (
          <iframe
            title={tx.title}
            className="tx-spotify"
            src={`https://open.spotify.com/embed/album/${tx.spotifyId}?utm_source=generator&theme=0`}
            width="100%"
            height="152"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
          />
        )}
      </div>
      <span className="tx-year">{tx.year}</span>
    </div>
  );
}

function Starfield() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let stars = [];
    let orbs = [];
    const STAR_COUNT = 140;
    const ORB_COUNT = 5;
    let animId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const init = () => {
      resize();
      stars = Array.from({ length: STAR_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.1 + 0.2,
        speed: Math.random() * 0.002 + 0.0008,
        phase: Math.random() * Math.PI * 2,
        dx: (Math.random() - 0.5) * 0.08,
        dy: (Math.random() - 0.5) * 0.06,
      }));
      orbs = Array.from({ length: ORB_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 60 + 30,
        speed: Math.random() * 0.0004 + 0.0002,
        phase: Math.random() * Math.PI * 2,
        dx: (Math.random() - 0.5) * 0.15,
        dy: (Math.random() - 0.5) * 0.1,
      }));
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw floating orbs (behind stars)
      orbs.forEach((o) => {
        o.x += o.dx;
        o.y += o.dy;
        if (o.x < -o.r) o.x = canvas.width + o.r;
        if (o.x > canvas.width + o.r) o.x = -o.r;
        if (o.y < -o.r) o.y = canvas.height + o.r;
        if (o.y > canvas.height + o.r) o.y = -o.r;
        const alpha = 0.015 + 0.025 * (0.5 + 0.5 * Math.sin(t * o.speed + o.phase));
        const grad = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r);
        grad.addColorStop(0, `rgba(201,168,76,${alpha * 2})`);
        grad.addColorStop(0.5, `rgba(201,168,76,${alpha})`);
        grad.addColorStop(1, `rgba(201,168,76,0)`);
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      });

      // Draw drifting stars
      stars.forEach((s) => {
        s.x += s.dx;
        s.y += s.dy;
        if (s.x < 0) s.x = canvas.width;
        if (s.x > canvas.width) s.x = 0;
        if (s.y < 0) s.y = canvas.height;
        if (s.y > canvas.height) s.y = 0;
        const alpha = 0.1 + 0.4 * (0.5 + 0.5 * Math.sin(t * s.speed + s.phase));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${alpha})`;
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };

    window.addEventListener("resize", init);
    init();
    animId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", init);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <div className="cosmos">
      <canvas ref={canvasRef} />
    </div>
  );
}

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const count = String(TRANSMISSIONS.length).padStart(3, "0");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="trav-root">
      <div className="portrait-bg">
        <img src="/avatar.jpg" alt="" />
      </div>
      <Starfield />

      <nav className={scrolled ? "scrolled" : ""}>
        <a href="/" className="nav-brand">
          <span className="nav-name">Travmissions</span>
          {TRANSMISSIONS.length > 0 && <span className="nav-freq">{`001–${count}`}</span>}
        </a>
        <ul className="nav-links">
          {TRANSMISSIONS.length > 0 && <li><a href="#archive">Archive</a></li>}
          <li><a href="#tunein">Tune In</a></li>
        </ul>
      </nav>

      <section className="hero">
        <div className="hero-signal">Now Transmitting</div>
        <h1 className="hero-title">Travmissions</h1>
        <p className="hero-def">
          Streams of consciousness —<br />
          <em>sound</em>, <em>word</em>, <em>thought</em>
        </p>
      </section>

      <div className="divider" />

      {TRANSMISSIONS.length > 0 ? (
        <section className="archive" id="archive">
          <div className="archive-header">
            <span className="archive-label">Transmissions</span>
            <div className="archive-line" />
            <span className="archive-count">{count} entries</span>
          </div>
          {TRANSMISSIONS.map((tx) => (
            <Transmission key={tx.id} tx={tx} />
          ))}
        </section>
      ) : (
        <section className="archive" id="archive">
          <div className="archive-header">
            <span className="archive-label">Transmissions</span>
            <div className="archive-line" />
          </div>
          <p className="incoming">First transmission incoming</p>
        </section>
      )}

      <div className="divider" />

      <section className="tunein" id="tunein">
        <h2 className="tunein-title">Tune In</h2>
        <p className="tunein-sub">Unravel Music on your platform</p>
        <div className="tunein-links">
          <a href={LINKS.spotify} target="_blank" rel="noopener noreferrer" className="tunein-link">
            <svg viewBox="0 0 24 24"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
            Spotify
          </a>
          <a href={LINKS.apple} target="_blank" rel="noopener noreferrer" className="tunein-link">
            <svg viewBox="0 0 24 24"><path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043A5.022 5.022 0 0019.7.165 10.104 10.104 0 0017.802.043C17.337.015 16.873 0 16.408 0H7.594c-.468 0-.93.015-1.397.042A9.918 9.918 0 004.3.165a5.022 5.022 0 00-1.874.725C1.309 1.633.564 2.633.247 3.943A9.23 9.23 0 00.007 6.124c-.028.462-.043.924-.043 1.389v8.974c0 .465.015.928.043 1.389.04.738.12 1.47.24 2.19.317 1.31 1.062 2.31 2.18 3.043.596.39 1.222.64 1.874.725.63.08 1.264.115 1.898.122.465.027.93.042 1.395.042h8.812c.468 0 .93-.015 1.397-.042a9.918 9.918 0 001.898-.122 5.022 5.022 0 001.874-.725c1.118-.733 1.863-1.733 2.18-3.043.12-.72.2-1.452.24-2.19.028-.46.043-.924.043-1.389V7.513c0-.465-.015-.927-.043-1.389z"/></svg>
            Apple Music
          </a>
          <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" className="tunein-link">
            <svg viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            Instagram
          </a>
        </div>
      </section>

      <footer>
        <div className="footer-name">Travmissions</div>
        <div className="footer-sub">© 2026 · All frequencies reserved</div>
      </footer>
    </div>
  );
}
